import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  CircleAlert,
  Cloud,
  Database,
  FileCheck2,
  FileSpreadsheet,
  FolderOpen,
  Inbox,
  LoaderCircle,
  Mail,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { requireFirebaseFunctions } from '../lib/firebase';
import type { ProcessingAvailability } from '../lib/processing-data';

const MONTHS = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
];

type DriveWorkspace = {
  folder: {
    id: string;
    name: string;
    webViewLink?: string;
    canAddChildren: boolean;
  };
  fileCount: number;
  folderCount: number;
  truncated: boolean;
};

type ReportMailboxStatus = {
  connected: boolean;
  mailboxEmail: string;
  recipientEmail: string;
  watch: {
    active: boolean;
    expirationAt: string | null;
    lastProcessedAt: string | null;
  };
};

type OverviewData = {
  workspace: DriveWorkspace | null;
  availability: ProcessingAvailability | null;
  mailbox: ReportMailboxStatus | null;
  failedServices: Array<'drive' | 'datasets' | 'mailbox'>;
};

type OverviewState =
  | { status: 'loading'; data: OverviewData | null }
  | { status: 'ready'; data: OverviewData }
  | { status: 'error'; data: OverviewData };

const demoData: OverviewData = {
  workspace: {
    folder: { id: 'demo-root', name: 'Report', canAddChildren: false },
    fileCount: 6,
    folderCount: 5,
    truncated: false,
  },
  availability: {
    masterYears: [2026, 2025, 2024],
    mail: [{
      year: 2026,
      months: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      latestReferenceDate: '2026-09-22',
      latestFileName: 'Report 22.09.2026.xlsx',
    }],
    responses: [{ year: 2026, months: [9] }],
  },
  mailbox: {
    connected: true,
    mailboxEmail: 'francesco.pace@justbit.it',
    recipientEmail: 'reportcrm-of@justbit.it',
    watch: {
      active: true,
      expirationAt: '2026-09-29T08:00:00.000Z',
      lastProcessedAt: '2026-09-22T18:14:20.584Z',
    },
  },
  failedServices: [],
};

function normalizeAvailability(value: ProcessingAvailability | null | undefined): ProcessingAvailability {
  return {
    masterYears: Array.isArray(value?.masterYears) ? value.masterYears : [],
    mail: Array.isArray(value?.mail) ? value.mail : [],
    responses: Array.isArray(value?.responses) ? value.responses : [],
  };
}

function greetingName(user: ReturnType<typeof useAuth>['user']): string {
  const explicitName = user?.firstName?.trim();
  if (explicitName && !explicitName.includes('@')) return explicitName.split(/\s+/)[0];

  const displayName = user?.displayName?.trim();
  if (displayName && !displayName.includes('@')) return displayName.split(/\s+/)[0];

  const emailName = user?.email?.split('@')[0]?.split(/[._-]+/)[0] ?? '';
  return emailName ? `${emailName[0].toLocaleUpperCase('it-IT')}${emailName.slice(1)}` : 'Utente';
}

function formatReferenceDate(value: string | null | undefined, compact = false): string {
  if (!value) return 'Non disponibile';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return 'Non disponibile';
  return new Intl.DateTimeFormat('it-IT', compact
    ? { day: '2-digit', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Mai';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Non disponibile';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function freshnessLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const reference = new Date(`${value}T12:00:00`);
  if (Number.isNaN(reference.getTime())) return '—';
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const days = Math.max(0, Math.round((today.getTime() - reference.getTime()) / 86_400_000));
  if (days === 0) return 'Oggi';
  if (days === 1) return 'Ieri';
  return `${days} giorni fa`;
}

function latestMailEntry(availability: ProcessingAvailability | null) {
  return (availability?.mail ?? []).reduce<(ProcessingAvailability['mail'][number] | null)>((latest, entry) => (
    !latest || entry.latestReferenceDate > latest.latestReferenceDate ? entry : latest
  ), null) ?? null;
}

export function DashboardPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState<OverviewState>({ status: 'loading', data: null });
  const [coverageYear, setCoverageYear] = useState<number | ''>('');
  const firstName = greetingName(user);

  const loadOverview = useCallback(async () => {
    setOverview((current) => ({ status: 'loading', data: current.data }));

    if (user?.demo) {
      setOverview({ status: 'ready', data: demoData });
      return;
    }

    const functions = requireFirebaseFunctions();
    const [workspaceResult, availabilityResult, mailboxResult] = await Promise.allSettled([
      httpsCallable<void, DriveWorkspace>(functions, 'getDriveWorkspace')(),
      httpsCallable<void, ProcessingAvailability>(functions, 'getProcessingAvailability')(),
      httpsCallable<void, ReportMailboxStatus>(functions, 'getReportMailboxStatus')(),
    ]);
    const failedServices: OverviewData['failedServices'] = [];
    if (workspaceResult.status === 'rejected') failedServices.push('drive');
    if (availabilityResult.status === 'rejected') failedServices.push('datasets');
    if (mailboxResult.status === 'rejected') failedServices.push('mailbox');

    const data: OverviewData = {
      workspace: workspaceResult.status === 'fulfilled' ? workspaceResult.value.data : null,
      availability: availabilityResult.status === 'fulfilled'
        ? normalizeAvailability(availabilityResult.value.data)
        : null,
      mailbox: mailboxResult.status === 'fulfilled' ? mailboxResult.value.data : null,
      failedServices,
    };
    setOverview({ status: failedServices.length === 3 ? 'error' : 'ready', data });
  }, [user?.demo]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadOverview(), 0);
    return () => window.clearTimeout(timeout);
  }, [loadOverview]);

  const data = overview.data;
  const availability = data?.availability ?? null;
  const latestMail = useMemo(() => latestMailEntry(availability), [availability]);
  const availableYears = useMemo(() => {
    if (!availability) return [];
    return [...new Set([
      ...availability.masterYears,
      ...availability.mail.map(({ year }) => year),
      ...availability.responses.map(({ year }) => year),
    ])].sort((left, right) => right - left);
  }, [availability]);

  const effectiveCoverageYear = coverageYear && availableYears.includes(coverageYear)
    ? coverageYear
    : availableYears[0] ?? '';
  const coverageMail = effectiveCoverageYear
    ? availability?.mail.find(({ year }) => year === effectiveCoverageYear)?.months ?? []
    : [];
  const coverageResponses = effectiveCoverageYear
    ? availability?.responses.find(({ year }) => year === effectiveCoverageYear)?.months ?? []
    : [];
  const hasMasterForLatestMail = Boolean(
    latestMail && availability?.masterYears.includes(latestMail.year),
  );
  const hasResponsesForLatestMail = Boolean(
    latestMail && availability?.responses.some(({ year, months }) => (
      year === latestMail.year && months.includes(Number(latestMail.latestReferenceDate.slice(5, 7)))
    )),
  );
  const reportsReady = Boolean(latestMail && hasMasterForLatestMail);
  const automationActive = data?.mailbox?.connected === true && data.mailbox.watch.active;
  const latestMaster = availability?.masterYears[0];

  return (
    <main className="dashboard-page overview-page">
      <header className="page-heading overview-heading">
        <div>
          <p className="app-eyebrow">Centro operativo</p>
          <h1>Bentornato, {firstName}.</h1>
          <p>Stato reale dei dati, copertura del periodo e scorciatoie per arrivare al report.</p>
        </div>
        <button className="button button-secondary overview-refresh" type="button" disabled={overview.status === 'loading'} onClick={() => void loadOverview()}>
          <RefreshCw className={overview.status === 'loading' ? 'spin' : undefined} size={16} />
          Aggiorna
        </button>
      </header>

      {overview.status === 'error' && (
        <div className="overview-error" role="alert">
          <CircleAlert size={17} />
          <span>Non riesco a leggere lo stato dei servizi. Riprova tra qualche istante.</span>
          <button type="button" onClick={() => void loadOverview()}>Riprova</button>
        </div>
      )}

      <section className={`overview-hero${overview.status === 'loading' && !data ? ' loading' : ''}`} aria-labelledby="latest-report-title">
        <div className="overview-hero-copy">
          <p className="overview-kicker"><span /> Ultimo report disponibile</p>
          {overview.status === 'loading' && !data ? (
            <div className="overview-loading-line"><LoaderCircle className="spin" size={19} /> Leggo i dati operativi…</div>
          ) : latestMail ? (
            <>
              <div className="overview-report-date">
                <strong>{formatReferenceDate(latestMail.latestReferenceDate)}</strong>
                <span>{freshnessLabel(latestMail.latestReferenceDate)}</span>
              </div>
              <h2 id="latest-report-title">{latestMail.latestFileName}</h2>
              <p>Ricevuto per il periodo più recente e pronto per essere controllato in Source.</p>
            </>
          ) : (
            <>
              <div className="overview-report-date"><strong>Nessun report Mail</strong></div>
              <h2 id="latest-report-title">Il prossimo file comparirà qui</h2>
              <p>Controlla la ricezione automatica oppure verifica che il file sia presente su Drive.</p>
            </>
          )}
          <div className="overview-hero-actions">
            <Link className="button button-primary" to="/app/elaborazioni">
              <Database size={16} /> Esplora i dati
            </Link>
            <Link className="overview-inline-link" to={reportsReady ? '/app/output' : '/app/sorgenti'}>
              {reportsReady ? 'Vai ai report' : 'Controlla Drive'} <ArrowRight size={15} />
            </Link>
          </div>
        </div>

        <aside className="overview-readiness" aria-label="Prerequisiti del report">
          <header>
            <div>
              <p>Prontezza dati</p>
              <h2>{reportsReady ? 'Puoi lavorare sui report' : 'Serve ancora un dato'}</h2>
            </div>
            <span className={reportsReady ? 'ready' : 'attention'}>
              {reportsReady ? <Check size={14} /> : <CircleAlert size={14} />}
              {reportsReady ? 'Pronto' : 'Attenzione'}
            </span>
          </header>
          <div className="overview-readiness-list">
            <div className={hasMasterForLatestMail ? 'complete' : ''}>
              <span>{hasMasterForLatestMail ? <Check size={14} /> : '1'}</span>
              <p><strong>Master annuale</strong><small>{latestMail ? `${latestMail.year}.xlsx` : 'Anno da determinare'}</small></p>
            </div>
            <div className={latestMail ? 'complete' : ''}>
              <span>{latestMail ? <Check size={14} /> : '2'}</span>
              <p><strong>Report giornaliero</strong><small>{formatReferenceDate(latestMail?.latestReferenceDate, true)}</small></p>
            </div>
            <div className={hasResponsesForLatestMail ? 'complete' : ''}>
              <span>{hasResponsesForLatestMail ? <Check size={14} /> : '3'}</span>
              <p><strong>Risposte del reparto</strong><small>{hasResponsesForLatestMail ? 'Periodo coperto' : 'Dato integrativo non disponibile'}</small></p>
            </div>
          </div>
          <footer>
            <ShieldCheck size={15} />
            <span>La generazione usa solo dati disponibili e tracciati.</span>
          </footer>
        </aside>
      </section>

      <section className="overview-metrics" aria-label="Indicatori operativi">
        <article>
          <span className="overview-metric-icon coral"><CalendarDays size={19} /></span>
          <div><p>Freschezza dati</p><strong>{freshnessLabel(latestMail?.latestReferenceDate)}</strong><small>{formatReferenceDate(latestMail?.latestReferenceDate, true)}</small></div>
        </article>
        <article>
          <span className="overview-metric-icon blue"><Mail size={19} /></span>
          <div><p>Copertura Mail {effectiveCoverageYear || ''}</p><strong>{coverageMail.length} mesi</strong><small>{coverageMail.length ? `Ultimo: ${MONTHS[Math.max(...coverageMail) - 1]}` : 'Nessun mese disponibile'}</small></div>
        </article>
        <article>
          <span className="overview-metric-icon mint"><FileSpreadsheet size={19} /></span>
          <div><p>Master più recente</p><strong>{latestMaster ?? '—'}</strong><small>{availability?.masterYears.length ?? 0} annualità disponibili</small></div>
        </article>
        <article>
          <span className="overview-metric-icon violet"><FolderOpen size={19} /></span>
          <div><p>Workspace Drive</p><strong>{data?.workspace?.fileCount ?? '—'} file</strong><small>{data?.workspace ? `${data.workspace.folderCount} cartelle indicizzate` : 'Inventario non disponibile'}</small></div>
        </article>
      </section>

      <section className="overview-main-grid">
        <article className="overview-panel overview-coverage">
          <header className="overview-panel-heading">
            <div>
              <p>Disponibilità mensile</p>
              <h2>Copertura dei dati</h2>
            </div>
            <label>
              <span>Anno</span>
              <select value={effectiveCoverageYear} disabled={!availableYears.length} onChange={(event) => setCoverageYear(Number(event.target.value))}>
                {!availableYears.length && <option value="">—</option>}
                {availableYears.map((year) => <option value={year} key={year}>{year}</option>)}
              </select>
            </label>
          </header>
          <div className="overview-coverage-body">
            <div className="overview-coverage-summary">
              <span className={effectiveCoverageYear && availability?.masterYears.includes(effectiveCoverageYear) ? 'available' : ''}>
                <FileSpreadsheet size={18} />
              </span>
              <div>
                <strong>Master {effectiveCoverageYear || '—'}</strong>
                <p>{effectiveCoverageYear && availability?.masterYears.includes(effectiveCoverageYear) ? 'Disponibile e pronto per le analisi annuali' : 'File annuale non disponibile'}</p>
              </div>
            </div>
            <div className="overview-month-grid" role="list" aria-label={`Copertura mensile ${effectiveCoverageYear || ''}`}>
              {MONTHS.map((month, index) => {
                const monthNumber = index + 1;
                const hasMail = coverageMail.includes(monthNumber);
                const hasResponse = coverageResponses.includes(monthNumber);
                return (
                  <div className={`${hasMail ? 'has-mail' : ''}${hasResponse ? ' has-response' : ''}`} role="listitem" key={month} title={`${month}: ${hasMail ? 'report Mail disponibile' : 'report Mail assente'}${hasResponse ? ', risposte reparto disponibili' : ''}`}>
                    <span>{month}</span>
                    <strong>{hasMail ? <Check size={15} /> : '–'}</strong>
                    {hasResponse && <i aria-label="Risposte reparto disponibili" />}
                  </div>
                );
              })}
            </div>
          </div>
          <footer className="overview-coverage-legend">
            <span><i className="mail" /> Report Mail</span>
            <span><i className="responses" /> Risposte reparto</span>
            <Link to="/app/elaborazioni">Apri le tabelle <ArrowRight size={14} /></Link>
          </footer>
        </article>

        <article className="overview-panel overview-next-actions">
          <header className="overview-panel-heading">
            <div>
              <p>Percorso consigliato</p>
              <h2>Le prossime azioni</h2>
            </div>
          </header>
          <div className="overview-action-list">
            <Link to="/app/elaborazioni">
              <span><Inbox size={18} /></span>
              <div><strong>Controlla l’ultimo import</strong><small>Filtra e verifica le righe ricevute</small></div>
              <ArrowRight size={15} />
            </Link>
            <Link to="/app/output">
              <span><BarChart3 size={18} /></span>
              <div><strong>Genera i report</strong><small>Trend, case in entrata e social subreason</small></div>
              <ArrowRight size={15} />
            </Link>
            <Link to="/app/validazione">
              <span><FileCheck2 size={18} /></span>
              <div><strong>Valida il risultato</strong><small>Confronta con il report manuale</small></div>
              <ArrowRight size={15} />
            </Link>
          </div>
        </article>
      </section>

      <section className="overview-services" aria-label="Stato dei servizi">
        <header>
          <div>
            <p>Infrastruttura</p>
            <h2>Servizi collegati</h2>
          </div>
          {data?.failedServices.length ? <span className="attention"><CircleAlert size={14} /> Stato parziale</span> : <span><Check size={14} /> Tutto operativo</span>}
        </header>
        <div>
          <article>
            <span className={data?.workspace ? 'active' : ''}><Cloud size={19} /></span>
            <p><strong>Google Drive</strong><small>{data?.workspace ? `${data.workspace.folder.name} accessibile` : 'Connessione non verificata'}</small></p>
            <b>{data?.workspace ? 'Online' : 'Da verificare'}</b>
          </article>
          <article>
            <span className={automationActive ? 'active' : ''}><Mail size={19} /></span>
            <p><strong>Ricezione automatica</strong><small>{automationActive ? `Ultima attività ${formatDateTime(data?.mailbox?.watch.lastProcessedAt)}` : 'Controlla il collegamento Gmail'}</small></p>
            <b>{automationActive ? 'Attiva' : 'Da configurare'}</b>
          </article>
          <article>
            <span className={availability ? 'active' : ''}><Database size={19} /></span>
            <p><strong>Catalogo dati</strong><small>{availability ? `${availableYears.length} annualità rilevate` : 'Disponibilità non rilevata'}</small></p>
            <b>{availability ? 'Aggiornato' : 'Da verificare'}</b>
          </article>
        </div>
      </section>
    </main>
  );
}
